import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useProgress } from '../contexts/ProgressContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AnalyticsService from '../services/analyticsService';
import Header from './Header';
import PromptCard from './PromptCard';
import ActionButton from './ActionButton';

const Prompts = ({ onNavigateToHome, onNavigateToCourses, onNavigateToProfile, onNavigateToWritersRoom, onLogOut, onNavigateToPrompt, onModeSwitch, showAdminMode = false }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lengthFilter, setLengthFilter] = useState('');
  
  const { getLessonStatus } = useProgress();
  const { user, currentSession, userOrganizations } = useAuth();
  const { themeColors } = useTheme();
  
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const promptsRef = collection(db, 'prompts');
        const snapshot = await getDocs(promptsRef);
        
        const promptsData = [];
        snapshot.forEach(doc => {
          promptsData.push({ id: doc.id, ...doc.data() });
        });
        
        setPrompts(promptsData);
        setLoading(false);
      } catch (error) {
        // EFFICIENT: Silent error handling, provide empty state
        setPrompts([]);
        setLoading(false);
      }
    };

    fetchPrompts();
  }, []);

  const handlePromptClick = async (prompt) => {
    // Track prompt click analytics
    if (user && currentSession && userOrganizations) {
      await AnalyticsService.trackAccess(
        'prompt',
        prompt.id,
        currentSession,
        user.uid,
        userOrganizations,
        {
          promptTitle: prompt.title || 'Untitled Prompt',
          promptDescription: prompt.body || prompt.description || 'No description available',
          action: 'prompt_clicked'
        }
      );
    }
    
    if (onNavigateToPrompt) {
      onNavigateToPrompt(prompt);
    }
  };

  // Get prompt status for progress tracking
  const getPromptStatus = (promptId) => {
    const status = getLessonStatus(promptId);
    return status; // 'not_started', 'active', or 'completed'
  };

  // Filter prompts based on selected filters
  const filteredPrompts = prompts.filter(prompt => {
    const categoryMatch = !categoryFilter || prompt.category === categoryFilter;
    const lengthMatch = !lengthFilter || prompt.length === lengthFilter;
    return categoryMatch && lengthMatch;
  });

  // Clear all filters
  const clearFilters = () => {
    setCategoryFilter('');
    setLengthFilter('');
  };

  const hasActiveFilters = categoryFilter && lengthFilter;

  if (loading) {
    return (
      <div>
        <Header 
          title="Writing Prompts"
          onNavigateToHome={onNavigateToHome}
          onNavigateToCourses={onNavigateToCourses}
          onNavigateToProfile={onNavigateToProfile}
          onNavigateToPrompts={() => {}} // No-op since we're already on prompts
          onNavigateToWritersRoom={onNavigateToWritersRoom}
          onLogOut={onLogOut}
          onModeSwitch={onModeSwitch}
          showAdminMode={showAdminMode}
        />
        {/* role=status announces loading to screen readers (WCAG 4.1.3) */}
        <div role="status" aria-live="polite" className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading prompts...</div>
        </div>
      </div>
    );
  }

  // Filter UI component
  const FilterControls = ({ isCentered = false }) => {
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [lengthDropdownOpen, setLengthDropdownOpen] = useState(false);
    const categoryDropdownRef = useRef(null);
    const lengthDropdownRef = useRef(null);
    const categoryButtonRef = useRef(null);
    const lengthButtonRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (categoryDropdownRef.current && categoryButtonRef.current &&
            !categoryDropdownRef.current.contains(event.target) &&
            !categoryButtonRef.current.contains(event.target)) {
          setCategoryDropdownOpen(false);
        }
        if (lengthDropdownRef.current && lengthButtonRef.current &&
            !lengthDropdownRef.current.contains(event.target) &&
            !lengthButtonRef.current.contains(event.target)) {
          setLengthDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const categoryOptions = [
      { value: '', label: 'All Categories' },
      { value: 'Revealing Characters', label: 'Revealing Characters' },
      { value: 'Literary Devices', label: 'Literary Devices' },
      { value: 'Showing vs. Telling', label: 'Showing vs. Telling' },
      { value: 'Setting', label: 'Setting' },
      { value: 'Scene Construction', label: 'Scene Construction' },
      { value: 'Dialogue', label: 'Dialogue' },
      { value: 'Hooks', label: 'Hooks' },
      { value: 'Scene Endings', label: 'Scene Endings' }
    ];

    const lengthOptions = [
      { value: '', label: 'All Lengths' },
      { value: '5 minutes', label: '5 minutes' },
      { value: '10 minutes', label: '10 minutes' },
      { value: '20 minutes', label: '20 minutes' }
    ];

    const getCategoryLabel = () => {
      if (!categoryFilter) return '';
      const option = categoryOptions.find(opt => opt.value === categoryFilter);
      return option ? option.label : '';
    };

    const getLengthLabel = () => {
      if (!lengthFilter) return '';
      const option = lengthOptions.find(opt => opt.value === lengthFilter);
      return option ? option.label : '';
    };

    return (
      <div className={`p-6 ${isCentered ? '' : 'mb-8'}`}>
        <div className={`flex flex-wrap items-center justify-center gap-4 ${isCentered ? 'flex-col sm:flex-row' : ''}`}>
          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium whitespace-nowrap" style={{ color: themeColors.textPrimary }}>
              I'd like a prompt about
            </span>
            <div className="relative">
              <button
                ref={categoryButtonRef}
                onClick={() => {
                  setCategoryDropdownOpen(!categoryDropdownOpen);
                  setLengthDropdownOpen(false);
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') { setCategoryDropdownOpen(false); categoryButtonRef.current?.focus(); } }}
                aria-expanded={categoryDropdownOpen}
                aria-controls="category-dropdown"
                aria-label={`Filter by category: ${getCategoryLabel() || 'Any'}`}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                style={{ color: themeColors.textPrimary }}
              >
                <span className="text-lg min-w-[120px] inline-block border-b pb-0.5" style={{ borderColor: themeColors.textPrimary }}>{getCategoryLabel()}</span>
                <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu — disclosure widget; plain div so no listbox keyboard contract */}
              {categoryDropdownOpen && (
                <div 
                  id="category-dropdown"
                  aria-label="Category options"
                  ref={categoryDropdownRef}
                  className="absolute top-full left-0 mt-2 w-64 rounded-lg shadow-xl border py-2 max-h-96 overflow-y-auto z-50"
                  style={{ 
                    backgroundColor: themeColors.card, 
                    borderColor: themeColors.border 
                  }}
                >
                  {categoryOptions.map((option) => {
                    const isSelected = categoryFilter === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          setCategoryFilter(option.value);
                          setCategoryDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 transition-colors duration-200 ${
                          isSelected ? 'font-semibold' : ''
                        }`}
                        style={{ 
                          color: themeColors.textPrimary,
                          backgroundColor: isSelected ? themeColors.borderSubtle : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = themeColors.borderSubtle;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Length Filter */}
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium whitespace-nowrap" style={{ color: themeColors.textPrimary }}>
              that I can do in
            </span>
            <div className="relative">
              <button
                ref={lengthButtonRef}
                onClick={() => {
                  setLengthDropdownOpen(!lengthDropdownOpen);
                  setCategoryDropdownOpen(false);
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') { setLengthDropdownOpen(false); lengthButtonRef.current?.focus(); } }}
                aria-expanded={lengthDropdownOpen}
                aria-controls="length-dropdown"
                aria-label={`Filter by length: ${getLengthLabel() || 'Any'}`}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                style={{ color: themeColors.textPrimary }}
              >
                <span className="text-lg min-w-[100px] inline-block border-b pb-0.5" style={{ borderColor: themeColors.textPrimary }}>{getLengthLabel()}</span>
                <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu — disclosure widget; plain div so no listbox keyboard contract */}
              {lengthDropdownOpen && (
                <div 
                  id="length-dropdown"
                  aria-label="Length options"
                  ref={lengthDropdownRef}
                  className="absolute top-full left-0 mt-2 w-56 rounded-lg shadow-xl border py-2 max-h-96 overflow-y-auto z-50"
                  style={{ 
                    backgroundColor: themeColors.card, 
                    borderColor: themeColors.border 
                  }}
                >
                  {lengthOptions.map((option) => {
                    const isSelected = lengthFilter === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          setLengthFilter(option.value);
                          setLengthDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 transition-colors duration-200 ${
                          isSelected ? 'font-semibold' : ''
                        }`}
                        style={{ 
                          color: themeColors.textPrimary,
                          backgroundColor: isSelected ? themeColors.borderSubtle : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = themeColors.borderSubtle;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm border rounded-md transition-colors whitespace-nowrap"
              style={{ 
                color: themeColors.textPrimary, 
                borderColor: themeColors.border,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.borderSubtle;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Results Summary */}
        {hasActiveFilters && (
          <div className="mt-4 text-sm text-center" style={{ color: themeColors.textSecondary }}>
            Showing {filteredPrompts.length} of {prompts.length} prompts
            {lengthFilter && ` (${lengthFilter})`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Header 
        title="Writing Prompts"
        onNavigateToHome={onNavigateToHome}
        onNavigateToCourses={onNavigateToCourses}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
        showAdminMode={showAdminMode}
      />
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto pt-4 md:pt-6 pb-12 px-4 sm:px-6 lg:px-8">
        {hasActiveFilters ? (
          <>
            {/* Filter Controls at Top */}
            <FilterControls isCentered={false} />

            {/* Prompts Grid - Even rows of 4 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center">
              {filteredPrompts.map((prompt) => {
                const promptStatus = getPromptStatus(prompt.id);
                
                return (
                  <PromptCard 
                    key={prompt.id} 
                    prompt={prompt} 
                    onClick={handlePromptClick}
                    promptStatus={promptStatus}
                  />
                );
              })}
            </div>

            {/* No Results Message */}
            {filteredPrompts.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  <p className="text-lg font-medium mb-2" style={{ color: themeColors.textPrimary }}>No prompts match your filters</p>
                  <p className="text-sm mb-4" style={{ color: themeColors.textSecondary }}>Try adjusting your filter criteria or clearing the filters</p>
                  <ActionButton
                    onClick={clearFilters}
                    size="sm"
                  >
                    Clear Filters
                  </ActionButton>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Centered Filter Controls */
          <div className="min-h-[calc(100vh-100px)] flex flex-col justify-center">
            <FilterControls isCentered={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Prompts;
